import { BaseComponent } from './base-component.js'
import { customElement, property, state, queryAssignedNodes } from 'lit/decorators.js'
import { html, css, CSSResultGroup, TemplateResult } from 'lit'

/**
 * slots:
 *  - header
 *  - body
 *  - footer
 */
@customElement('il-modal')
export default class ModalComponent extends BaseComponent {
  static override events = {
    CLOSE_MODAL: 'closeModal'
  }

  @property({ attribute: 'header-title', type: String })
  headerTitle = undefined

  @property({ attribute: 'header-text', type: String })
  headerText = undefined

  @property({ attribute: 'no-border', type: Boolean })
  noBorder = false

  // if you don't need any padding on the modal body, set 'noPaddingOnBody' as true
  @property({ attribute: 'no-padding-on-body', type: Boolean })
  noPaddingOnBody = false

  // set this as true if you need your modal to stay open when clicking on the overlay
  @property({ attribute: 'stay-open-on-overlay-click', type: Boolean })
  stayOpenOnOverlayClick = false

  @property({ attribute: 'is-open', type: Boolean })
  isOpen = false

  @queryAssignedNodes({ slot: 'footer', flatten: true })
  private _footerNodes?: Array<Node>

  @state()
  private _hasFooter = false

  @property({ type: Function })
  onClose?: () => {} = undefined

  private onCloseEventListener: (event: Event) => void = (_event) => {
    this.isOpen = false
  }

  override connectedCallback (): void {
    super.connectedCallback()

    this.addEventListener(ModalComponent.events.CLOSE_MODAL, this.onCloseEventListener, {
      capture: true
    })
  }

  override disconnectedCallback (): void {
    super.disconnectedCallback()

    this.removeEventListener(ModalComponent.events.CLOSE_MODAL, this.onCloseEventListener, {
      capture: true
    })
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-modal-container {
        position: fixed;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;

        display: flex;
        align-items: center;
        justify-content: center;
      }

      .il-modal-container.il-modal-container--no-border .il-modal {
        border: none;
      }

      .il-modal-container.il-modal-container--no-padding-on-body .il-modal__body {
        padding: 0;
      }

      .il-modal-container.il-modal-container--footer-visible .il-modal__footer {
        display: block;
      }

      .il-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: var(--il-color-white-opacity-75);
      }

      .il-modal {
        position: relative;
        max-height: calc(100% - 2rem);
        width: calc(100% - 2rem);
        max-width: 618px;

        background-color: var(--il-color-neutrals-0);
        box-shadow: var(--il-shadow-cool-base);
        border: 2px solid var(--il-color-accent-100);
        border-radius: 12px;
        overflow: auto;
      }

      .il-modal__header {
        position: sticky;
        top: 0;
        padding: 1rem;
        z-index: 1;

        border-bottom: 1px solid var(--il-color-neutrals-100);
        background-color: inherit;
      }

      .il-modal__header__title {
        font-size: var(--il-font-size-20);
        font-weight: var(--il-font-weight-xbold);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-modal__header__text {
        margin-top: .75rem;

        color: var(--il-color-neutrals-300);
        font-size: var(--il-font-size-14);
        font-weight: var(--il-font-weight-bold);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-2);
      }

      .il-modal__body {
        padding: .75rem 1rem;
      }

      .il-modal__footer {
        display: none;
        position: sticky;
        bottom: 0;
        padding: 1rem;
        z-index: 1;

        border-top: 1px solid var(--il-color-neutrals-100);
        background-color: inherit;
      }
    `
  ]

  override render () {
    if (this.isOpen) {
      return html`
        <div
          class="
            ${this.baseCSSClass}
            il-modal-container
            ${this.isOpen ? 'il-modal-container--open' : ''}
            ${this.noBorder ? 'il-modal-container--no-border' : ''}
            ${this.noPaddingOnBody ? 'il-modal-container--no-padding-on-body' : ''}
            ${this._hasFooter ? 'il-modal-container--footer-visible' : ''}
          "
          part="modal-container"
          data-test-iliad-modal
        >
          <div
            class="il-modal-overlay"
            @click=${() => this._onOverlayClick()}
            data-test-iliad-modal-overlay
          ></div>

          <div class="il-modal" part="modal">
            ${this._renderHeader()}

            <div class="il-modal__body" part="modal-body" data-test-iliad-modal-body>
              <slot name="body"></slot>
            </div>

            <div class="il-modal__footer" part="modal-footer" data-test-iliad-modal-footer>
              <slot name="footer" @slotchange="${this._onFooterSlotChange}"></slot>
            </div>
          </div>
        </div>
      `
    } else {
      return html``
    }
  }

  private _onOverlayClick (): void {
    if (!this.stayOpenOnOverlayClick) {
      this._onClose()
    }
  }

  private _onClose (): void {
    this.onClose?.()
  }

  private _renderHeader (): TemplateResult {
    if (this.headerTitle || this.headerText) {
      let headerTemplate = html`
        <div class="il-modal__header">
          <div class="il-modal__header__title" part="modal-header-title">
            ${this.headerTitle}
          </div>
        </div>
      `

      if (this.headerText) {
        headerTemplate = html`
          <div class="il-modal__header">
            <div class="il-modal__header__title" part="modal-header-title">
              ${this.headerTitle}
            </div>

            <div class="il-modal__header__text" part="modal-header-text">
              ${this.headerText}
            </div>
          </div>
        `
      }

      return headerTemplate
    }

    return html`
      <div class="il-modal__header">
        <slot name="header"></slot>
      </div>
    `
  }

  private _onFooterSlotChange () {
    this._hasFooter = this._footerNodes ? this._footerNodes.length > 0 : false
  }
}
